/**
 * Owner: Masa Bayu
 * File: text-figure.ts
 * Date:Time: 2026-09-20 21:16
 * Description: CA-06 / Phase 1D figure + caption block — identity-only assetRef.
 */
import { withQXTextModelBlocks } from './text-model';
/** CA-06 M1 image-class kinds allowed on figure assets. */
export const QX_FIGURE_ASSET_KINDS = ['image', 'svg'];
export function isQXFigureAssetKind(value) {
    return QX_FIGURE_ASSET_KINDS.includes(value);
}
export function createQXTextAssetRef(id) {
    if (typeof id !== 'string' || id.trim().length === 0) {
        throw new Error('assetRef.id must be a non-empty string');
    }
    return Object.freeze({ id });
}
export function createQXTextCaptionBlock(inlines = [{ type: 'text', text: '', marks: [] }], language) {
    if (language === undefined) {
        return {
            type: 'caption',
            inlines: [...inlines],
        };
    }
    return {
        type: 'caption',
        inlines: [...inlines],
        language,
    };
}
export function createQXTextFigureBlock(input = {}) {
    const figure = { type: 'figure' };
    if (input.assetRef !== undefined) {
        figure.assetRef = createQXTextAssetRef(input.assetRef.id);
    }
    if (input.alt !== undefined) {
        if (typeof input.alt !== 'string') {
            throw new Error('figure alt must be a string when provided');
        }
        figure.alt = input.alt;
    }
    if (input.caption !== undefined) {
        if (input.caption.type !== 'caption') {
            throw new Error('figure caption must be a caption block');
        }
        figure.caption = createQXTextCaptionBlock(input.caption.inlines, input.caption.language);
    }
    if (input.language !== undefined) {
        figure.language = input.language;
    }
    return Object.freeze(figure);
}
export function isQXTextFigureBlock(block) {
    return block.type === 'figure';
}
export function requireQXTextFigureBlock(block) {
    if (!isQXTextFigureBlock(block)) {
        throw new Error('expected figure block');
    }
    return block;
}
/** Build factory input from an existing figure, applying only defined patch keys. */
function figureInputFrom(figure, patch = {}) {
    const input = {};
    if (patch.assetRef === false) {
        // cleared
    }
    else if (patch.assetRef !== undefined) {
        input.assetRef = patch.assetRef;
    }
    else if (figure.assetRef !== undefined) {
        input.assetRef = figure.assetRef;
    }
    if (patch.alt === false) {
        // cleared
    }
    else if (patch.alt !== undefined) {
        input.alt = patch.alt;
    }
    else if (figure.alt !== undefined) {
        input.alt = figure.alt;
    }
    if (patch.caption === false) {
        // cleared
    }
    else if (patch.caption !== undefined) {
        input.caption = patch.caption;
    }
    else if (figure.caption !== undefined) {
        input.caption = figure.caption;
    }
    if (patch.language === false) {
        // cleared
    }
    else if (patch.language !== undefined) {
        input.language = patch.language;
    }
    else if (figure.language !== undefined) {
        input.language = figure.language;
    }
    return input;
}
function replaceBlock(model, blockIndex, next) {
    if (blockIndex < 0 || blockIndex > model.blocks.length) {
        throw new Error('block index out of range');
    }
    const blocks = model.blocks.slice();
    if (next === null) {
        if (blockIndex >= blocks.length) {
            throw new Error('block index out of range');
        }
        blocks.splice(blockIndex, 1);
        return withQXTextModelBlocks(model, blocks);
    }
    if (blockIndex === blocks.length) {
        blocks.push(next);
    }
    else {
        blocks[blockIndex] = next;
    }
    return withQXTextModelBlocks(model, blocks);
}
function readFigure(model, blockIndex) {
    const block = model.blocks[blockIndex];
    if (block === undefined) {
        throw new Error('block not found');
    }
    return requireQXTextFigureBlock(block);
}
/** Insert a figure at `blockIndex` (append when index === length). */
export function insertQXTextFigure(model, blockIndex, figure = createQXTextFigureBlock()) {
    if (figure.type !== 'figure') {
        throw new Error('insert figure requires a figure block');
    }
    if (blockIndex < 0 || blockIndex > model.blocks.length) {
        throw new Error('block index out of range');
    }
    const blocks = model.blocks.slice();
    blocks.splice(blockIndex, 0, createQXTextFigureBlock(figure));
    return withQXTextModelBlocks(model, blocks);
}
export function deleteQXTextFigure(model, blockIndex) {
    readFigure(model, blockIndex);
    return replaceBlock(model, blockIndex, null);
}
/**
 * Attach asset identity to figure. Does not create/delete Asset binaries.
 * When `kind` is supplied, must be image-class (image|svg) or reject.
 */
export function attachQXTextFigureAsset(model, blockIndex, assetId, kind) {
    if (kind !== undefined && !isQXFigureAssetKind(kind)) {
        throw new Error('unsupported media kind for figure asset');
    }
    const figure = readFigure(model, blockIndex);
    return replaceBlock(model, blockIndex, createQXTextFigureBlock(figureInputFrom(figure, { assetRef: createQXTextAssetRef(assetId) })));
}
export function replaceQXTextFigureAsset(model, blockIndex, assetId, kind) {
    const figure = readFigure(model, blockIndex);
    if (figure.assetRef === undefined) {
        throw new Error('figure has no asset to replace — use attach');
    }
    return attachQXTextFigureAsset(model, blockIndex, assetId, kind);
}
/** Detach asset reference from figure — does not delete Asset binary. */
export function detachQXTextFigureAsset(model, blockIndex) {
    const figure = readFigure(model, blockIndex);
    return replaceBlock(model, blockIndex, createQXTextFigureBlock(figureInputFrom(figure, { assetRef: false })));
}
/** Set figure alt string. Does not copy caption text (caption ≠ alt). */
export function setQXTextFigureAlt(model, blockIndex, alt) {
    if (typeof alt !== 'string') {
        throw new Error('figure alt must be a string');
    }
    const figure = readFigure(model, blockIndex);
    return replaceBlock(model, blockIndex, createQXTextFigureBlock(figureInputFrom(figure, { alt })));
}
export function attachQXTextFigureCaption(model, blockIndex, caption = createQXTextCaptionBlock()) {
    const figure = readFigure(model, blockIndex);
    if (figure.caption !== undefined) {
        throw new Error('figure already has a caption (≤1)');
    }
    return replaceBlock(model, blockIndex, createQXTextFigureBlock(figureInputFrom(figure, { caption })));
}
export function detachQXTextFigureCaption(model, blockIndex) {
    const figure = readFigure(model, blockIndex);
    return replaceBlock(model, blockIndex, createQXTextFigureBlock(figureInputFrom(figure, { caption: false })));
}
/**
 * Publish-time figure completeness (CA-06 M2/M5).
 * Authoring validation remains permissive for optional slots.
 */
export function validateQXTextFiguresForPublish(model, options) {
    const assetIds = options.assetIds instanceof Set
        ? options.assetIds
        : new Set(options.assetIds);
    const issues = [];
    model.blocks.forEach((block, index) => {
        const path = `blocks[${String(index)}]`;
        if (block.type === 'caption') {
            issues.push({
                code: 'caption-without-figure',
                path,
                message: 'caption is not allowed as a top-level block.',
            });
            return;
        }
        if (block.type !== 'figure') {
            return;
        }
        if (block.assetRef === undefined) {
            issues.push({
                code: 'missing-asset-ref',
                path: `${path}.assetRef`,
                message: 'publish requires figure.assetRef.',
            });
            return;
        }
        const id = block.assetRef.id;
        if (!assetIds.has(id)) {
            issues.push({
                code: 'dangling-asset-ref',
                path: `${path}.assetRef.id`,
                message: `figure assetRef "${id}" is not present on the Asset plane.`,
            });
        }
        if (block.alt === undefined || block.alt.trim().length === 0) {
            issues.push({
                code: 'missing-alt',
                path: `${path}.alt`,
                message: 'publish requires non-empty figure.alt when assetRef is present.',
            });
        }
        if (options.resolveKind) {
            const kind = options.resolveKind(id);
            if (kind === undefined || !isQXFigureAssetKind(kind)) {
                issues.push({
                    code: 'unsupported-media-kind',
                    path: `${path}.assetRef`,
                    message: `figure asset kind must be image-class (image|svg); found ${String(kind)}.`,
                });
            }
        }
    });
    return issues;
}
//# sourceMappingURL=text-figure.js.map